import { useState } from 'react';
import { NextSeo } from 'next-seo';
import { Formik, Form, Field } from 'formik';
import * as yup from 'yup';
import { AppRoutes } from 'src/lib/routes';
import { useRouter } from 'next/router';
import useFlash from 'src/hooks/useFlash';
import useCsrfTokenUpdate from 'src/hooks/useCsrfTokenUpdate';
import useRedirectIfLoggedIn from 'src/hooks/useRedirectIfLoggedIn';
import useCurrentUser from 'src/hooks/useCurrentUser';
import Loading from 'src/components/Loading';
import ApiClient from 'src/lib/api_client';
import useEnhanceApiClient from 'src/hooks/useEnhanceApiClient';
import { saveLastLoginMethod } from 'src/lib/last_login_method'
import { useTranslations } from 'next-intl';
import { prepareTranslations } from 'src/lib/prepare_translations';
import { GetStaticProps } from 'next';
import ErrorMesseageTranslation from 'src/components/ErrorMesseageTranslation';

const ValidationSchema = yup.object().shape({
  email: yup.string().required('validationEmailRequired').typeError('validationEmailTypeError'),
});

const Page: React.FC = () => {
  const translation = useTranslations('pages/users/password/new')
  const sendPasswordResetInstruction = useEnhanceApiClient(ApiClient.sendPasswordResetInstruction);
  const [, setFlash] = useFlash();
  const router = useRouter();
  const [error, setError] = useState<string>();
  const updateCsrfToken = useCsrfTokenUpdate();
  const [currentUser, , , , isLoading] = useCurrentUser();

  useRedirectIfLoggedIn();

  return isLoading || currentUser ? (
    <Loading className="tw-justify-center tw-py-20" />
  ) : (
    <>
      <NextSeo title={translation('metaTitle')} noindex={true} nofollow={true} />

      <div className="tw-bg-brand-yellow tw-py-16 md:tw-py-24" style={{ minHeight: 'calc(100vh - 422px)' }}>
        <div className="container tw-max-w-md tw-w-full">
          <h2 className="tw-text-2xl md:tw-text-4xl tw-text-center tw-mb-6 tw-font-semibold tw-font-styleSans">{translation('recoverPassword')}</h2>
          <div className="tw-my-6">{translation('enterYourAccount')}</div>

          <Formik
            validationSchema={ValidationSchema}
            initialValues={{
              email: '',
            }}
            onSubmit={(values, { setSubmitting }) => {
              updateCsrfToken()
                .then((token) => {
                  sendPasswordResetInstruction({ user: values }, { 'X-CSRF-Token': token })
                    .then((result) => {
                      const [json] = result
                      if ('error' in json) {
                        setError(json.error);
                      } else {
                        setFlash({
                          notice: translation('youWillReceive'),
                        });
                        saveLastLoginMethod(undefined)
                        router.push(AppRoutes.loginRoute({}, {}).toUrl());
                      }
                    })
                    .catch((e) => {
                      setError(translation('anInternalError'));
                      throw e;
                    })
                    .finally(() => setSubmitting(false));
                })
                .catch((e) => {
                  setError(e.message);
                  throw e;
                });
            }}
          >
            {(formik) => (
              <Form>
                {error && <div className="tw-mb-4 tw-text-red'">{error}</div>}

                <div className="form-group">
                  <div className="input-group">
                    <Field
                      name="email"
                      placeholder={translation('email')}
                      autofocus={true}
                      className="form-control"
                    />
                  </div>
                  <ErrorMesseageTranslation name="email" />
                </div>

                <div className="tw-text-center">
                  <button type="submit" className="btn btn-primary">
                    {translation('sendResetLink')}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const translations = await prepareTranslations(context, ['pages/users/password/new']);

  return { props: translations }
};

export default Page;
